import React, {FC} from 'react';
import {Subject} from 'rxjs';

import {ContactsManagerFrame} from '../../libs/contacts-manager/src/lib/ui/web/contactsManagerFrame';
import {KnowledgeBaseFrame} from '../../libs/knowledge-base/web/src/knowledgeBaseFrame';
import {AnalyticsFrame} from '../analytics/analyticsFrame';
import {SingleUseValue} from '../helpers/memoryHelpers';
import {AppContentTypesEnum} from '../store/panels/app/parameters/appParameters';
import {
  AppPanelId,
  makeAnalyticsPanelId,
  makeCalendarsPanelId,
  makeComposerPanelId,
  makeContactsManagerPanelId,
  makeInboxesPanelId,
  makeKnowledgeBasePanelId,
  makeViewerPanelId,
} from '../store/panels/panelStateBase';
import {TopBar} from './app/topBar/topBar';
import {CalendarsFrame} from './calendars/calendarsFrame';
import {GrammarlyWrapper} from './grammarly/grammarlyWrapper';
import {StandaloneComposerContainer} from './inboxes/composer/standaloneComposerContainer';
import {InboxesFrame} from './inboxes/inboxesFrame';
import {LegacyFrameContainer} from './legacy/legacyFrameContainer';
import {LegacyFrameRawBoot} from './legacy/legacyFrameHelpers';
import {PopupsContainer} from './popups/popupsContainer';
import {VisitorExperience} from './visitors/visitorExperience';
import {VisitorExperienceV3} from './visitors/visitorExperienceV3';
import {VisitorViewerContainer} from './visitors/visitorViewerContainer';

/*
 * Props.
 */

interface AppContentProps {
  panelId: AppPanelId;
  contentType: AppContentTypesEnum;
  rawBoot$?: Subject<LegacyFrameRawBoot>;
  initialRawBoot?: SingleUseValue<unknown>;
}

/*
 * Component.
 */

export const AppContent: FC<AppContentProps> = (props) => {
  const {panelId, contentType, rawBoot$, initialRawBoot} = props;

  switch (contentType) {
    case AppContentTypesEnum.STANDALONE_COMPOSER:
      return (
        <>
          <StandaloneComposerContainer panelId={makeComposerPanelId(panelId)} />
          <PopupsContainer />
        </>
      );

    case AppContentTypesEnum.VISITOR_EXPERIENCE:
      return <VisitorExperience panelId={makeInboxesPanelId(panelId)} />;
    case AppContentTypesEnum.VISITOR_EXPERIENCE_V3:
      return <VisitorExperienceV3 panelId={makeInboxesPanelId(panelId)} />;

    case AppContentTypesEnum.VISITOR_VIEWER:
      return (
        <>
          <VisitorViewerContainer panelId={makeViewerPanelId(panelId)} />
          <PopupsContainer />
        </>
      );

    default: {
      const isInboxesVisible = contentType === AppContentTypesEnum.INBOXES;
      const isCalendarsVisible = contentType === AppContentTypesEnum.CALENDARS;
      const isContactsVisible = contentType === AppContentTypesEnum.CONTACTS;
      const isAnalyticsVisible = contentType === AppContentTypesEnum.ANALYTICS;
      const isKnowledgeBaseVisible = contentType === AppContentTypesEnum.KNOWLEDGE_BASE;
      const isLegacyFrameVisible = !(
        isInboxesVisible ||
        isCalendarsVisible ||
        isContactsVisible ||
        isAnalyticsVisible ||
        isKnowledgeBaseVisible
      );

      return (
        <>
          <TopBar panelId={panelId} contentType={contentType} />
          <LegacyFrameContainer
            panelId={panelId}
            rawBoot$={rawBoot$ || new Subject()}
            isVisible={isLegacyFrameVisible}
            initialRawBoot={initialRawBoot}
          >
            <CalendarsFrame panelId={makeCalendarsPanelId(panelId)} isVisible={isCalendarsVisible}>
              <AnalyticsFrame panelId={makeAnalyticsPanelId(panelId)} isVisible={isAnalyticsVisible}>
                <ContactsManagerFrame
                  panelId={makeContactsManagerPanelId(panelId)}
                  isVisible={isContactsVisible}
                >
                  <KnowledgeBaseFrame
                    isVisible={isKnowledgeBaseVisible}
                    panelId={makeKnowledgeBasePanelId(panelId)}
                  >
                    <GrammarlyWrapper>
                      <InboxesFrame panelId={makeInboxesPanelId(panelId)} isVisible={isInboxesVisible} />
                      <PopupsContainer />
                    </GrammarlyWrapper>
                  </KnowledgeBaseFrame>
                </ContactsManagerFrame>
              </AnalyticsFrame>
            </CalendarsFrame>
          </LegacyFrameContainer>
        </>
      );
    }
  }
};
